/**
 * The typed API client.
 *
 * `hc<AppType>` derives its paths, path parameters, request bodies and response
 * types from the API's own route definitions, so a renamed field or a mistyped route
 * is a compile error here rather than a runtime surprise. `AppType` is exported by
 * `apps/console/api`; the type import is erased at build time, so no server code — Hono,
 * Drizzle, the provider adapters — reaches the browser bundle.
 *
 * Two things the hand-written client did that this keeps:
 *
 *  - **same-origin requests**, so the session cookie rides along and nothing is
 *    stored in JavaScript where a cross-site script could read it;
 *  - **one error type**, `ApiError`, carrying the server's code when it sent one and
 *    a derived code when it did not — the screens branch on it, so it must not change
 *    shape just because the transport did.
 */
import { hc } from 'hono/client'
import type { ClientResponse } from 'hono/client'
import type { ResponseFormat } from 'hono/types'
import type { StatusCode, SuccessStatusCode } from 'hono/utils/http-status'
import type { AppType } from '@api/app'
import { errorFromResponse, networkError } from './api'

export interface RpcOptions {
	/** Defaults to `/api`, the same-origin prefix the web app's Worker proxies. */
	baseUrl?: string
	fetchImpl?: typeof fetch
}

export function createRpc(options: RpcOptions = {}) {
	const baseUrl = (options.baseUrl ?? '/api').replace(/\/$/, '')
	const doFetch = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args))

	return hc<AppType>(baseUrl, {
		fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
			try {
				return await doFetch(input, { ...init, credentials: 'same-origin' })
			} catch (cause) {
				// Thrown, not returned: a fetch rejection has no Response to inspect, and
				// the caller must not have to distinguish the two shapes.
				throw networkError(cause)
			}
		},
	})
}

/** The client the app uses. Tests build their own with `createRpc`. */
export const rpc = createRpc()

/**
 * The client's type, for screens that receive one.
 *
 * Screens take a client rather than importing the singleton so a test can hand them
 * one built over a stub `fetch` — which exercises the real client, the real
 * serialisation and the real error mapping, instead of a hand-written fake of them.
 */
export type RpcClient = ReturnType<typeof createRpc>

/**
 * Unwraps a response, or throws the `ApiError` the screens branch on.
 *
 * The alternative — checking `res.ok` at 39 call sites — is the boilerplate this
 * replaces, and it is the kind of boilerplate where one missed check turns a failure
 * into `undefined` flowing into the UI.
 */
/**
 * The body of the successful members of a response union.
 *
 * `hc` types a call as a union of one response per status the route can return, and
 * the successful member's status is `ContentfulStatusCode` — every 2xx *and* every
 * error — because a handler returning `c.json(body)` names no status. Its `ok` is
 * therefore `boolean`, and `if (res.ok)` narrows nothing.
 *
 * Filtering on the status instead distributes over that union properly: the 2xx
 * members contribute their body, the rest contribute `never`.
 */
type SuccessBody<R> =
	R extends ClientResponse<infer T, infer S, infer F> ? (S extends SuccessStatusCode ? (F extends 'text' ? never : T) : never) : never

export async function unwrap<R extends ClientResponse<unknown, StatusCode, ResponseFormat>>(response: R): Promise<SuccessBody<R>> {
	if (!response.ok) throw await errorFromResponse(response)
	// The runtime `ok` check above is what makes this the successful member; the cast
	// records what the status filter already proved to the type system.
	return (await response.json()) as SuccessBody<R>
}
